import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Clock,
  Star,
  CheckCircle,
  Book,
  Code,
  Layers,
  FileCode,
  ShieldAlert,
  Boxes,
  FileText,
  Monitor,
  Database,
  Rocket,
} from "lucide-react";
import { Link } from "react-router-dom";

const JavaProgramming = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  // Inside syllabusItems
  const syllabusItems = [
    {
      title: "Java Basics & Data Types",
      desc: "Understand variables, data types, operators, memory handling, and Java's type safety features.",
      icon: (
        <Book size={36} className="text-blue-600 animate-bounce mx-auto mb-2" />
      ),
      shadow: "shadow-blue-300",
    },
    {
      title: "Loops & Conditions",
      desc: "Control program flow using if-else, switch cases, and various loop constructs.",
      icon: (
        <Code size={36} className="text-purple-600 animate-spin mx-auto mb-2" />
      ),
      shadow: "shadow-purple-300",
    },
    {
      title: "Arrays & Strings",
      desc: "Manipulate arrays and strings using Java’s built-in classes and methods efficiently.",
      icon: (
        <Layers
          size={36}
          className="text-green-600 animate-pulse mx-auto mb-2"
        />
      ),
      shadow: "shadow-green-300",
    },
    {
      title: "OOP Concepts",
      desc: "Implement Object-Oriented principles such as classes, objects, inheritance, and abstraction.",
      icon: (
        <FileCode
          size={36}
          className="text-red-500 animate-[wiggle_1s_ease-in-out_infinite] mx-auto mb-2"
        />
      ),
      shadow: "shadow-red-300",
    },
    {
      title: "Exception Handling",
      desc: "Use try-catch-finally blocks to handle errors and create custom exception classes.",
      icon: (
        <ShieldAlert
          size={36}
          className="text-yellow-500 animate-bounce mx-auto mb-2"
        />
      ),
      shadow: "shadow-yellow-300",
    },
    {
      title: "Collections Framework",
      desc: "Work with Lists, Sets, Maps using ArrayList, HashMap, TreeSet and learn when to use each.",
      icon: (
        <Boxes size={36} className="text-pink-600 animate-ping mx-auto mb-2" />
      ),
      shadow: "shadow-pink-300",
    },
    {
      title: "File I/O & Streams",
      desc: "Read/write data using InputStream/OutputStream, and handle files with FileReader/Writer.",
      icon: (
        <FileText
          size={36}
          className="text-indigo-600 animate-[spin_3s_linear_infinite] mx-auto mb-2"
        />
      ),
      shadow: "shadow-indigo-300",
    },
    {
      title: "GUI with Java Swing",
      desc: "Build user interfaces using JFrame, JButton, JTextField, JTable and event handling.",
      icon: (
        <Monitor
          size={36}
          className="text-teal-500 animate-pulse mx-auto mb-2"
        />
      ),
      shadow: "shadow-teal-300",
    },
    {
      title: "JDBC + MySQL Integration",
      desc: "Perform CRUD operations and transactions in Java applications using JDBC and MySQL.",
      icon: (
        <Database
          size={36}
          className="text-orange-500 animate-bounce mx-auto mb-2"
        />
      ),
      shadow: "shadow-orange-300",
    },
    {
      title: "Final Mini Project",
      desc: "Build a complete desktop app (e.g., inventory system) integrating GUI and database.",
      icon: (
        <Rocket
          size={36}
          className="text-green-700 animate-[wiggle_1s_ease-in-out_infinite] mx-auto mb-2"
        />
      ),
      shadow: "shadow-green-400",
    },
  ];

  return (
    <div className="bg-gray-50 text-gray-900 mt-10">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-gray-800 to-gray-900 text-white py-20 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right">
            <h1 className="text-5xl font-extrabold mb-6 leading-tight">
              Java Programming Course
            </h1>
            <p className="text-lg text-gray-200 mb-6">
              Learn Java from scratch, master OOP, build real-time GUI apps, and
              become job-ready with practical projects.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-xl font-bold text-base shadow-lg hover:bg-yellow-300 transition"
            >
              Enroll Now 🚀
            </Link>
          </div>
          <div className="flex justify-center" data-aos="fade-left">
            <img
              src="../assets/java-4.svg"
              alt="Java Logo"
              className="w-72 h-72 object-contain animate-bounce"
            />
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Course Details
        </h2>
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div
            className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl shadow hover:shadow-lg transition transform hover:scale-105"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <Clock
              className="text-blue-500 mx-auto mb-2 animate-bounce"
              size={36}
            />
            <h5 className="font-semibold">Duration</h5>
            <p>4 Months</p>
          </div>

          <div
            className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-6 rounded-xl shadow hover:shadow-lg transition transform hover:-translate-y-2"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <Star
              className="text-yellow-400 mx-auto mb-2 animate-spin"
              size={36}
              fill="currentColor"
            />
            <h5 className="font-semibold">Rating</h5>
            <p>4.8 / 5</p>
          </div>

          <div
            className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl shadow hover:shadow-lg transition transform hover:rotate-3"
            data-aos="zoom-in"
            data-aos-delay="300"
          >
            <CheckCircle
              className="text-green-500 mx-auto mb-2 animate-[wiggle_1s_ease-in-out_infinite]"
              size={36}
            />
            <h5 className="font-semibold">Projects</h5>
            <p>GUI & DB Apps</p>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          What You’ll Learn
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {syllabusItems.map((item, i) => {
            const animations = [
              "fade-up",
              "zoom-in",
              "flip-left",
              "flip-right",
              "fade-down",
              "zoom-out",
              "fade-left",
              "fade-right",
              "flip-up",
              "flip-down",
            ];
            const animation = animations[i % animations.length];

            // Different background colors
            const colors = [
              "bg-blue-100",
              "bg-green-100",
              "bg-yellow-100",
              "bg-pink-100",
              "bg-purple-100",
              "bg-indigo-100",
              "bg-red-100",
              "bg-teal-100",
              "bg-orange-100",
              "bg-emerald-100",
            ];
            const cardColor = colors[i % colors.length];

            return (
              <div
                key={i}
                className={`${cardColor} rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:scale-105`}
                data-aos={animation}
                data-aos-delay={i * 100}
              >
                {item.icon}
                <h5 className="font-semibold text-lg mb-2">{item.title}</h5>
                <p className="text-gray-800">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Instructor */}
      {/* <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Meet Your Instructor
        </h2>
        <div
          className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl shadow text-center p-8 hover:shadow-lg transition"
          data-aos="flip-up"
        >
          <img
            src="https://t3.ftcdn.net/jpg/02/65/18/30/360_F_265183061_NkulfPZgRxbNg3rvYSNGGwi0iD7qbmOp.jpg"
            alt="Instructor"
            className="w-28 h-28 rounded-full mx-auto mb-4 object-cover animate-[wiggle_1s_ease-in-out_infinite]"
          />
          <h5 className="font-semibold">Mr. Arun Kumar</h5>
          <p className="text-gray-600">
            Senior Java Developer with 10+ years of teaching and industry
            experience.
          </p>
        </div>
      </section> */}

      {/* Call to Action */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center rounded-2xl shadow-lg p-10"
          data-aos="zoom-in"
        >
          <h2 className="text-3xl font-bold mb-3">
            Start Your Java Career Now!
          </h2>
          <p className="text-lg mb-6">
            Enroll today for hands-on lessons, real-time projects, and
            certification.
          </p>
          <Link
            to="/contact"
            className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-lg shadow hover:bg-gray-100 transition animate-bounce"
          >
            Enroll Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JavaProgramming;
